import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { RED, YELLOW } from '../utils/brandColors';
import { EASE_OUT, DUR } from '../utils/motion';

// One continuous axis shared by both options.
//
// The reveal used to give each card its own left-anchored fill, so the
// head-to-head read as two unrelated bars that happened to sit near each
// other. A single meter makes the result one quantity: it starts at a 50/50
// dead heat and *resolves* toward the winner, so the motion itself is the
// verdict rather than decoration on top of it.
//
// Reused at smaller heights elsewhere (post rows, archetype evidence), hence
// the `height` prop.
//
// Two modes:
//  - head-to-head (default): RED fill against a YELLOW remainder, parked at
//    50/50 until `reveal` — the vote-result meter described above.
//  - single-fill: pass `fillColor` (and optionally `trackColor`) for a plain
//    progress bar. Width tracks `percentA` directly with no dead-heat park,
//    because progress is not a contest between two options.
//
// The single-fill mode is not new API for its own sake: TierLadder,
// ArchetypeBlock and StreakModal were already passing `fillColor`/`trackColor`
// while this component silently dropped them, so every progress bar in the
// streak sheet rendered as a half-red-half-yellow vote split frozen at 50/50.

export default function SplitMeter({ percentA, reveal, reducedMotion, height, ariaLabel, fillColor, trackColor }) {
  const clamped = Math.max(0, Math.min(100, Number(percentA) || 0));
  const transition = reducedMotion ? 'none' : `width ${DUR.bar}ms ${EASE_OUT}`;
  const singleFill = Boolean(fillColor);
  const fillWidth = singleFill || reveal ? `${clamped}%` : '50%';

  return (
    <Box
      role="img"
      aria-label={ariaLabel}
      sx={{
        display: 'flex',
        width: '100%',
        height,
        borderRadius: '999px',
        overflow: 'hidden',
        bgcolor: trackColor || 'action.disabledBackground',
      }}
    >
      <Box
        data-testid="split-meter-a"
        sx={{
          width: fillWidth,
          bgcolor: singleFill ? fillColor : RED,
          transition,
        }}
      />
      {!singleFill && <Box data-testid="split-meter-b" sx={{ flex: 1, bgcolor: YELLOW }} />}
    </Box>
  );
}

SplitMeter.propTypes = {
  percentA: PropTypes.number,
  reveal: PropTypes.bool,
  reducedMotion: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ariaLabel: PropTypes.string,
  fillColor: PropTypes.string,
  trackColor: PropTypes.string,
};

SplitMeter.defaultProps = {
  percentA: 50,
  reveal: false,
  reducedMotion: false,
  height: { xs: 10, sm: 12 },
  ariaLabel: undefined,
  fillColor: undefined,
  trackColor: undefined,
};
